"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { clsx } from "clsx";
import { cn } from "@ui/lib/utils";
import { captureEvent } from "@/app/_analytics/analytics";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";

import IconWriting from "@ui/central-icons/icons/IconWriting";
import IconInfinity from "@ui/central-icons/icons/IconInfinity";
import IconMinimize45 from "@ui/central-icons/icons/IconMinimize45";
import IconArrowsRepeatCircle from "@ui/central-icons/icons/IconArrowsRepeatCircle";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";

import Image from "next/image";
import { Button } from "../core/Button";
import { LinkTracked } from "../core/LinkTracked";
import { Checkbox } from "../core/Checkbox";
import { SubmitCircleButton } from "../core/SubmitCircleButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../core/Tooltip";
import { useAudioPlayer } from "./AudioPlayerContext";
import { EventName } from "@mw/shared/analyticsEventNames";

export interface GeneratedSoundItem {
  id: string;
  title: string;
  coverImage?: string;
  /** Blob URL or remote URL for audio playback. When provided, a play button is shown. */
  audioUrl?: string;
  isLoop?: boolean;
}

export interface SuggestionPill {
  /** Short label displayed inside the pill button. */
  title: string;
  /** Full prompt text inserted into the textarea when the pill is clicked. */
  prompt: string;
}

interface AudioGenerationWidgetLabels {
  pillText: string;
  headerText: string;
  placeholder: string;
  loopOn?: string;
  loopOff?: string;
  loopTooltip?: string;
}

interface AudioGenerationWidgetEventNames {
  signUpCtaClicked: EventName;
  suggestionPillClicked?: EventName;
}

interface AudioGenerationWidgetProps {
  onSubmit?: (prompt: string) => void;
  generateItems?: (prompt: string, loop: boolean) => Promise<GeneratedSoundItem[]>;
  onDownload?: (item: GeneratedSoundItem) => void;
  onPlay?: (item: GeneratedSoundItem, index: number) => void;
  onGenerateNew?: () => void;
  signUpHref?: string;
  /** Pre-fill the prompt input. Applied when the component mounts or when the value changes. */
  initialPrompt?: string;
  /** Controlled expanded state. When provided, expand/collapse is controlled by the parent. */
  isExpanded?: boolean;
  /** Called when the widget's expanded state changes (controlled mode). */
  onExpandChange?: (expanded: boolean) => void;
  className?: string;
  /** Whether to show the loop toggle in the toolbar. Defaults to false. */
  showLoopToggle?: boolean;
  /** When true, the widget fills its container width instead of capping at md:31.25rem. */
  fullWidth?: boolean;
  /** Number of rows for the prompt textarea. Defaults to 3. */
  textareaRows?: number;
  /** Increment to programmatically focus the textarea (useful when isExpanded is always true). */
  focusKey?: number;
  /** Suggestion pills shown above the textarea in the input state. Clicking a pill fills the prompt. */
  suggestions?: SuggestionPill[];
  /** When true, clicking anywhere on a generation result row (outside action buttons) starts playback. */
  clickableResultRows?: boolean;
  /** Domain-specific labels passed by callers. */
  labels: AudioGenerationWidgetLabels;
  /** Domain-specific event names. Controls which PostHog event fires on the sign-up CTA. */
  eventNames: AudioGenerationWidgetEventNames;
}

const defaultGenerateItems = async (_prompt: string, _loop: boolean): Promise<GeneratedSoundItem[]> => {
  await new Promise(resolve => setTimeout(resolve, 2000));
  return [
    { id: "1", title: "Generation 1" },
    { id: "2", title: "Generation 2" },
    { id: "3", title: "Generation 3" },
    { id: "4", title: "Generation 4" },
  ];
};

const AudioGenerationWidget = ({
  onSubmit,
  generateItems,
  onDownload,
  onPlay,
  onGenerateNew,
  signUpHref,
  initialPrompt,
  isExpanded: isExpandedProp,
  onExpandChange,
  className,
  showLoopToggle = false,
  fullWidth = false,
  textareaRows = 3,
  focusKey,
  suggestions,
  clickableResultRows = false,
  labels,
  eventNames,
}: AudioGenerationWidgetProps) => {
  const t = useTranslations("ClientKeys.AudioGenerationWidget");
  const isControlled = isExpandedProp !== undefined;
  const [expandedInternal, setExpandedInternal] = useState(isExpandedProp ?? false);
  const expanded = isControlled ? isExpandedProp : expandedInternal;
  const [prompt, setPrompt] = useState(initialPrompt ?? "");
  const [loop, setLoop] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedItems, setGeneratedItems] = useState<GeneratedSoundItem[] | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const firstResultActionRef = useRef<HTMLButtonElement>(null);
  const generateNewButtonRef = useRef<HTMLButtonElement>(null);
  const generationIdRef = useRef(0);
  const wasExpandedRef = useRef(expanded);

  // Sync prompt when initialPrompt changes
  useEffect(() => {
    if (initialPrompt !== undefined) {
      setPrompt(initialPrompt);
      setGeneratedItems(null);
    }
  }, [initialPrompt]);

  const focusTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [textareaRef]);

  const focusResultsAction = useCallback(() => {
    if (firstResultActionRef.current) {
      firstResultActionRef.current.focus();
      return;
    }

    generateNewButtonRef.current?.focus();
  }, [firstResultActionRef, generateNewButtonRef]);

  // Focus textarea when focusKey increments (for inline/always-expanded mode)
  useEffect(() => {
    if (!focusKey) return;
    setGeneratedItems(null);
    const id = requestAnimationFrame(() => focusTextarea());
    return () => cancelAnimationFrame(id);
  }, [focusKey, focusTextarea]);

  // Focus textarea when expanded and generation is empty, or first available results action when not empty
  useEffect(() => {
    const wasExpanded = wasExpandedRef.current;
    wasExpandedRef.current = expanded;

    if (!expanded) return;

    const expandedJustOpened = !wasExpanded;
    const hasResults = generatedItems !== null;

    // Avoid stealing focus on mount when the widget is rendered inline and already expanded.
    if (!expandedJustOpened && !hasResults) return;

    if (!hasResults) {
      focusTextarea();
    } else {
      focusResultsAction();
    }
  }, [expanded, generatedItems, focusTextarea, focusResultsAction]);

  const setExpanded = (value: boolean) => {
    if (!isControlled) setExpandedInternal(value);
    onExpandChange?.(value);
  };

  const handleExpand = () => setExpanded(true);

  const handleCollapse = () => {
    generationIdRef.current += 1;
    setIsGenerating(false);
    stopActive();
    setExpanded(false);
  };

  const { handlePlay: playerHandlePlay, activeId, playState, stopActive } = useAudioPlayer();

  const handleSubmit = async () => {
    if (!prompt.trim() || isGenerating) return;
    const currentId = ++generationIdRef.current;
    setIsGenerating(true);
    const fn = generateItems ?? defaultGenerateItems;
    try {
      const items = await fn(prompt.trim(), loop);
      if (generationIdRef.current !== currentId) return;
      setGeneratedItems(items);
      onSubmit?.(prompt.trim());
    } catch {
      // Error is handled by resetting isGenerating in finally
    } finally {
      if (generationIdRef.current === currentId) {
        setIsGenerating(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleGenerateNew = () => {
    stopActive();
    setGeneratedItems(null);
    onGenerateNew?.();
  };

  const handleSuggestionClick = (pill: SuggestionPill) => {
    setPrompt(pill.prompt);
    focusTextarea();
    if (eventNames.suggestionPillClicked) {
      captureEvent(eventNames.suggestionPillClicked, { title: pill.title });
    }
  };

  const minimizeBtn = onExpandChange ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='pill'
            aria-label={t("minimize")}
            onClick={handleCollapse}
            className='tw-shrink-0 tw-rounded-full tw-bg-white tw-shadow-[0px_0px_0px_0.818px_rgba(0,0,0,0.06),0px_0.818px_1.637px_0px_rgba(0,0,0,0.04),0px_1.637px_3.274px_0px_rgba(0,0,0,0.04)] hover:tw-bg-black/5'
            icon={<IconMinimize45 className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />}
          />
        </TooltipTrigger>
        <TooltipContent variant='dark-new'>{t("minimize")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <div
      onClick={!expanded ? handleExpand : undefined}
      role={!expanded ? "button" : undefined}
      tabIndex={!expanded ? 0 : undefined}
      onKeyDown={
        !expanded
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleExpand();
              }
            }
          : undefined
      }
      className={cn(
        "tw-border-0.5 tw-border-solid",
        expanded
          ? [
              "tw-relative tw-flex tw-flex-col tw-gap-2 tw-p-2 sm:tw-gap-2.5 sm:tw-p-3",
              "tw-rounded-[20px] sm:tw-rounded-[30px] tw-border-white tw-cursor-default",
              "tw-bg-white tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_6px_16px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]",
              fullWidth ? "tw-w-full" : "tw-w-full md:tw-w-[31.25rem]",
            ]
          : [
              "tw-flex tw-items-center tw-gap-2.5 tw-pl-2.5 tw-pr-7 tw-py-2.5",
              "tw-rounded-[30px] tw-border-[rgba(0,0,0,0.1)] tw-cursor-pointer",
              "tw-bg-[rgba(245,242,239,0.8)] tw-backdrop-blur-[30px]",
              "tw-shadow-[0px_6px_16px_0px_rgba(78,50,23,0.04)]",
            ],
        className,
      )}
    >
      {expanded ? (
        generatedItems !== null ? (
          /* Results view */
          <>
            {/* Header + results list */}
            <div className='tw-flex tw-flex-col tw-gap-0.5'>
              <div className='tw-flex tw-items-start tw-justify-between'>
                <p className='tw-pl-3 tw-pt-3 tw-opacity-60 tw-type-sm tw-text-gray-500'>
                  <span className='tw-block tw-truncate'>{labels.headerText}</span>
                </p>
                {minimizeBtn}
              </div>

              <div className='tw-flex tw-flex-col tw-px-3'>
                {generatedItems.map((item, index) => {
                  const isActive = activeId === item.id && playState !== "idle";
                  return (
                    <div key={item.id}>
                      <div
                        className={cn(
                          "tw-relative tw-flex tw-items-center tw-gap-4 tw-py-2 -tw-mx-3 tw-px-3 tw-rounded-lg tw-transition-colors",
                          clickableResultRows &&
                            item.audioUrl &&
                            "tw-cursor-pointer [&:hover:not(:has([data-action-buttons]:hover))]:tw-bg-[linear-gradient(90deg,_rgba(245,243,241,0)_0%,_#F5F3F1_8%,_#F5F3F1_92%,_rgba(245,243,241,0)_100%)]",
                        )}
                      >
                        {clickableResultRows && item.audioUrl && (
                          <button
                            className='tw-absolute tw-inset-0 tw-z-0 tw-rounded-lg'
                            aria-label={t("play")}
                            onClick={() => {
                              playerHandlePlay({ id: item.id, audioUrl: item.audioUrl!, loop: item.isLoop });
                              onPlay?.(item, index);
                            }}
                          />
                        )}
                        <div className='tw-pointer-events-none tw-relative tw-size-8 tw-rounded-[2px] tw-overflow-hidden tw-shrink-0 tw-bg-[#d0b2b2]'>
                          {item.coverImage && <Image src={item.coverImage} fill className='tw-object-cover' alt='' />}
                        </div>
                        <p className='tw-pointer-events-none tw-flex-1 tw-pr-24 tw-type-base tw-text-black'>
                          <span className='tw-block tw-truncate'>{t("generation", { number: index + 1 })}</span>
                        </p>
                        <div
                          data-action-buttons
                          className='tw-z-[1] tw-absolute tw-right-0 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-gap-0.5'
                        >
                          {item.audioUrl ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    ref={index === 0 ? firstResultActionRef : undefined}
                                    variant='ghost-warm'
                                    size='pill-responsive'
                                    aria-label={isActive ? t("pause") : t("play")}
                                    className='tw-opacity-60 hover:tw-opacity-100'
                                    icon={
                                      isActive ? (
                                        <IconPauseFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                                      ) : (
                                        <IconPlayFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                                      )
                                    }
                                    onClick={() => {
                                      playerHandlePlay({ id: item.id, audioUrl: item.audioUrl!, loop: item.isLoop });
                                      onPlay?.(item, index);
                                    }}
                                  />
                                </TooltipTrigger>
                                <TooltipContent variant='dark-new'>{isActive ? t("pause") : t("play")}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <div className='tw-w-8' aria-hidden='true' />
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  ref={index === 0 && !item.audioUrl ? firstResultActionRef : undefined}
                                  variant='ghost-warm'
                                  size='pill-responsive'
                                  aria-label={t("download")}
                                  className='tw-opacity-60 hover:tw-opacity-100'
                                  icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />}
                                  onClick={() => onDownload?.(item)}
                                />
                              </TooltipTrigger>
                              <TooltipContent variant='dark-new'>{t("download")}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                      {index < generatedItems.length - 1 && (
                        <div className='tw-border-b tw-border-dashed tw-border-black/10' />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom bar */}
            <div className='tw-flex tw-items-center tw-justify-between tw-h-11'>
              <button
                ref={generateNewButtonRef}
                type='button'
                onClick={handleGenerateNew}
                className='tw-flex tw-items-center tw-gap-1.5 tw-pl-2 tw-opacity-60 hover:tw-opacity-100 tw-transition-opacity'
              >
                <IconArrowsRepeatCircle className={cn(iconStrokeFix, "tw-size-4 tw-shrink-0 tw-text-black")} />
                <span className='tw-type-sm tw-text-black'>{t("generate_new")}</span>
              </button>
              {signUpHref && (
                <LinkTracked
                  href={signUpHref}
                  asButton
                  buttonVariants={{ variant: "primary", size: "lg" }}
                  eventName={eventNames.signUpCtaClicked}
                >
                  {t("sign_up")}
                </LinkTracked>
              )}
            </div>
          </>
        ) : (
          /* Input view */
          <>
            {/* Header row */}
            <div className='tw-flex tw-items-start tw-justify-between'>
              <div className='tw-flex tw-flex-col tw-gap-3 tw-flex-1'>
                <p className='tw-pl-3 tw-pt-3 tw-opacity-60 tw-type-sm tw-text-gray-500'>
                  <span className='tw-block tw-truncate'>{labels.headerText}</span>
                </p>
                {suggestions && suggestions.length > 0 && (
                  <div className='tw-flex tw-flex-wrap tw-gap-1.5 tw-px-3'>
                    {suggestions.map(pill => (
                      <button
                        key={pill.title}
                        type='button'
                        onClick={() => handleSuggestionClick(pill)}
                        className='tw-rounded-full tw-bg-cream tw-px-3 tw-py-1 tw-type-sm tw-text-black hover:tw-bg-[#EDEBE8] tw-transition-colors tw-shrink-0 tw-whitespace-nowrap tw-outline-none focus-visible:tw-outline-focus'
                      >
                        {pill.title}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={labels.placeholder}
                  aria-label={labels.headerText}
                  rows={textareaRows}
                  disabled={isGenerating}
                  className={clsx(
                    "tw-w-full tw-resize-none tw-bg-transparent tw-outline-none tw-border-none",
                    "tw-px-3 tw-pb-0.5 tw-type-base tw-text-black",
                    "placeholder:tw-text-black/30",
                    "tw-scrollbar-[rgba(0,0,0,0.15)] tw-scrollbar-thin",
                    isGenerating && "tw-opacity-50",
                  )}
                />
              </div>

              {/* Minimize button */}
              {minimizeBtn}
            </div>

            {/* Toolbar */}
            <div className='tw-flex tw-items-center tw-justify-between tw-h-11 tw-pl-2'>
              {showLoopToggle ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Checkbox
                        isSelected={loop}
                        onChange={v => setLoop(v)}
                        className='!tw-hidden'
                        wrapperClassName={clsx(
                          "tw-gap-[3px] tw-pl-1 tw-pr-1 tw-rounded tw-outline-none tw-transition-opacity tw-duration-150 tw-cursor-pointer",
                          "data-[focus-visible]:tw-outline-focus",
                          loop ? "tw-opacity-100 hover:tw-opacity-70" : "tw-opacity-40 hover:tw-opacity-60",
                        )}
                      >
                        <IconInfinity className={cn(iconStrokeFix, "tw-size-5 tw-text-black")} />
                        <span className='tw-type-sm tw-text-black'>
                          {loop ? (labels.loopOn ?? "") : (labels.loopOff ?? "")}
                        </span>
                      </Checkbox>
                    </TooltipTrigger>
                    <TooltipContent variant='dark-new'>{labels.loopTooltip ?? ""}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div />
              )}

              <SubmitCircleButton
                onClick={handleSubmit}
                disabled={!prompt.trim()}
                loading={isGenerating}
                aria-label={isGenerating ? t("generating") : t("submit")}
              />
            </div>
          </>
        )
      ) : (
        /* Collapsed pill */
        <>
          <div className='tw-size-10 tw-rounded-full tw-bg-black/5 tw-flex tw-items-center tw-justify-center tw-shrink-0'>
            <IconWriting className={cn(iconStrokeFix, "tw-size-6 tw-text-black")} />
          </div>
          <p className='tw-whitespace-nowrap tw-normal-case tw-type-base tw-text-black'>{labels.pillText}</p>
        </>
      )}
    </div>
  );
};

AudioGenerationWidget.displayName = "AudioGenerationWidget";

export { AudioGenerationWidget };
export type { AudioGenerationWidgetProps, AudioGenerationWidgetLabels, AudioGenerationWidgetEventNames };
